// Paso 3 — REPLANTEO
//
// KPI superior: paneles, m² cubiertos, total. Visor axonométrico Three.js
// (lo crea el agente de viz). Toggle vista cenital plana. Badge si aplica
// la regla del 5% (planta cuadrada) o del 15% (cubo).

import { state, update } from '/src/shared/state.js';
import { ctaButton } from '/src/ui/components/cta-button.js';
import { kpiRow } from '/src/ui/components/kpi-row.js';

async function loadViewer() {
  try {
    const mod = await import('/src/viz/axonometric.js');
    if (mod && typeof mod.createAxonometricViewer === 'function') {
      return mod.createAxonometricViewer;
    }
  } catch (_err) {
    /* todavía no existe */
  }
  return null;
}

async function loadCoverage() {
  try {
    const mod = await import('/src/core/coverage.js');
    return mod;
  } catch (_err) {
    return null;
  }
}

function detectRoomShape(room) {
  if (!room) return 'normal';
  const { largo, ancho, altura } = room;
  if (!largo || !ancho || !altura) return 'normal';
  const eq = (a, b) => Math.abs(a - b) / Math.max(a, b) < 0.02;
  if (eq(largo, ancho) && eq(largo, altura)) return 'cube';
  if (eq(largo, ancho)) return 'square';
  return 'normal';
}

export function renderStep3(host, { next, back }) {
  host.innerHTML = `
    <header class="step__header">
      <h1 class="step__title" id="step-3-title">Replanteo</h1>
      <p class="step__subtitle">Aquí tienes la distribución sugerida en tu sala.</p>
    </header>

    <div id="rule-badge"></div>
    <div id="kpi"></div>

    <div class="viewer">
      <button type="button" class="btn btn--secondary viewer__toggle" id="toggle-view">
        Vista cenital plana
      </button>
      <canvas id="axonometric" aria-label="Vista axonométrica de la sala con paneles"></canvas>
    </div>

    <div class="sticky-cta" id="sticky-cta"></div>
  `;

  const kpiHost = host.querySelector('#kpi');
  const ruleHost = host.querySelector('#rule-badge');
  const toggleBtn = host.querySelector('#toggle-view');
  const canvas = host.querySelector('#axonometric');
  const stickyHost = host.querySelector('#sticky-cta');

  // Paneles sugeridos: el módulo coverage devuelve cantidades por superficie.
  // Las convertimos en posiciones concretas (rejilla uniforme desde el centro).
  loadCoverage().then((cov) => {
    if (cov && typeof cov.suggestPanels === 'function' && state.product) {
      try {
        const result = cov.suggestPanels({ room: state.room, product: state.product });
        const panels = layoutPanels(state.room, state.product, result);
        update({ panels, coverage: result });
        // Reanclar viewer con los paneles ya colocados.
        if (currentHandle?.update) currentHandle.update(panels);
      } catch (err) {
        console.warn('[step3] suggestPanels falló:', err);
      }
    }
    refreshKPI();
  });

  function layoutPanels(room, product, cov) {
    const out = [];
    if (!room?.largo || !cov) return out;
    const formato = (product.formato || 'RECTANGULO').toUpperCase();
    const areaM2 = product.areaM2 || product.areaPerUnit || 0;
    // Lado del panel: si dimension viene como "AxB" lo parseamos; si no, raíz cuadrada del área.
    const dims = parseDim(product.dimension || product.dim || '');
    const panelW = dims.w || (areaM2 > 0 ? Math.sqrt(areaM2) : 0.5);
    const panelH = dims.h || (areaM2 > 0 ? Math.sqrt(areaM2) : 0.5);
    const color = '#a8a8a8';

    // Techo
    const ceilN = Math.max(0, cov.ceilingQuantity || 0);
    if (ceilN > 0) {
      const aspect = room.largo / room.ancho;
      let cols = Math.max(1, Math.round(Math.sqrt(ceilN * aspect)));
      let rows = Math.max(1, Math.ceil(ceilN / cols));
      while (rows * cols < ceilN) cols++;
      const margin = 0.3;
      const xStep = (room.largo - 2 * margin) / cols;
      const zStep = (room.ancho - 2 * margin) / rows;
      let placed = 0;
      for (let r = 0; r < rows && placed < ceilN; r++) {
        for (let c = 0; c < cols && placed < ceilN; c++) {
          out.push({
            position: { x: margin + xStep * (c + 0.5), y: room.altura, z: margin + zStep * (r + 0.5) },
            surface: 'ceiling',
            dimensions: { w: panelW, h: panelH },
            formato,
            color,
          });
          placed++;
        }
      }
    }

    // Paredes (solo si la sala es alta).
    // Convención del visor (axonometric.js):
    //   wall0 = pared en x=0       (varía z, normal +X)
    //   wall1 = pared en x=largo   (varía z, normal -X)
    //   wall2 = pared en z=0       (varía x, normal +Z)
    //   wall3 = pared en z=ancho   (varía x, normal -Z)
    if (Array.isArray(cov.wallQuantities)) {
      const wallSpecs = [
        { side: 'wall0', length: room.ancho, axis: 'z', x: 0 },
        { side: 'wall1', length: room.ancho, axis: 'z', x: room.largo },
        { side: 'wall2', length: room.largo, axis: 'x', z: 0 },
        { side: 'wall3', length: room.largo, axis: 'x', z: room.ancho },
      ];
      cov.wallQuantities.forEach((n, i) => {
        if (n <= 0) return;
        const spec = wallSpecs[i];
        const cols = Math.max(1, Math.round(Math.sqrt((n * spec.length) / room.altura)));
        const rows = Math.max(1, Math.ceil(n / cols));
        const margin = 0.3;
        const stepA = (spec.length - 2 * margin) / cols;
        const stepY = (room.altura - 2 * margin) / rows;
        let placed = 0;
        for (let r = 0; r < rows && placed < n; r++) {
          for (let c = 0; c < cols && placed < n; c++) {
            const a = margin + stepA * (c + 0.5);
            const y = margin + stepY * (r + 0.5);
            const pos = spec.axis === 'x'
              ? { x: a, y, z: spec.z }
              : { x: spec.x, y, z: a };
            out.push({
              position: pos,
              surface: spec.side,
              dimensions: { w: panelW, h: panelH },
              formato,
              color,
            });
            placed++;
          }
        }
      });
    }
    return out;
  }

  function parseDim(s) {
    const str = String(s || '').replace(',', '.');
    const m = str.match(/(\d+\.?\d*)\s*[x×]\s*(\d+\.?\d*)/i);
    if (!m) return { w: 0, h: 0 };
    let w = parseFloat(m[1]);
    let h = parseFloat(m[2]);
    if (w > 10) w /= 1000; // mm → m
    if (h > 10) h /= 1000;
    return { w, h };
  }

  function refreshKPI() {
    const panelsCount = Array.isArray(state.panels) ? state.panels.length : 0;
    const areaPer = state.product?.areaPerUnit || state.product?.areaM2 || 0;
    const m2 = panelsCount * areaPer;
    const price = panelsCount * (state.product?.price || state.product?.precio || 0);

    kpiHost.innerHTML = '';
    kpiHost.appendChild(
      kpiRow([
        { label: 'Paneles', value: String(panelsCount) },
        { label: 'Cubiertos', value: `${m2.toLocaleString('es-ES', { maximumFractionDigits: 2 })} m²` },
        { label: 'Total + IVA', value: `${price.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`, gold: true },
      ]),
    );
  }
  refreshKPI();

  // Badge de regla.
  const shape = detectRoomShape(state.room);
  if (shape === 'square') {
    ruleHost.innerHTML = `<div class="badge-rule">Planta cuadrada — añadimos un 5% extra de absorción</div>`;
  } else if (shape === 'cube') {
    ruleHost.innerHTML = `<div class="badge-rule">Sala cúbica — añadimos un 15% extra de absorción</div>`;
  }

  // Visor.
  let cleanupViewer = null;
  let topdown = false;
  let currentHandle = null;
  loadViewer().then((create) => {
    if (!create) {
      // Mensaje placeholder mientras viz no esté.
      const ctx = canvas.getContext && canvas.getContext('2d');
      if (ctx) {
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
        ctx.fillStyle = '#f4f5f7';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#848c9c';
        ctx.font = '14px Poppins, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Visor 3D en preparación', canvas.width / 2, canvas.height / 2);
      }
      return;
    }
    try {
      const handle = create(canvas, state.room, state.panels);
      currentHandle = handle;
      cleanupViewer = handle?.dispose || handle?.destroy || null;
      toggleBtn.addEventListener('click', () => {
        topdown = !topdown;
        toggleBtn.textContent = topdown ? 'Vista 3D' : 'Vista cenital plana';
        if (handle?.setView) handle.setView(topdown ? 'top' : 'iso');
        else if (handle?.setTopdown) handle.setTopdown(topdown);
      });
    } catch (err) {
      console.warn('[step3] axonometric viewer falló:', err);
    }
  });

  // CTAs
  const backBtn = ctaButton({ label: 'Atrás', variant: 'secondary', onClick: back });
  const nextBtn = ctaButton({ label: 'Ver cómo suena', variant: 'primary', onClick: next });
  stickyHost.appendChild(backBtn);
  stickyHost.appendChild(nextBtn);

  return () => {
    if (typeof cleanupViewer === 'function') {
      try {
        cleanupViewer();
      } catch (_err) {
        /* ignore */
      }
    }
  };
}
